/**
 * useQuotes.js — Quotes CRUD hooks
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../lib/queryClient';
import { api } from '../lib/api';
import { snakeToCamel, camelToSnake } from '../lib/utils';

export function useQuotes({ enabled = true } = {}) {
  return useQuery({
    queryKey: queryKeys.quotes.all,
    queryFn: async () => {
      const rows = await api.get('/quotes');
      return rows.map(snakeToCamel);
    },
    enabled,
  });
}

export function useQuote(id) {
  return useQuery({
    queryKey: queryKeys.quotes.detail(id),
    queryFn: async () => {
      const row = await api.get(`/quotes/${id}`);
      return snakeToCamel(row);
    },
    enabled: !!id,
  });
}

export function useCreateQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => api.post('/quotes', camelToSnake(data)),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.quotes.all });
      // Audit row is written server-side with a before/after diff
      // (server/routes/crud.js) — logging here as well produced two rows per save.
    },
  });
}

export function useUpdateQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }) => api.put(`/quotes/${id}`, camelToSnake(data)),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.quotes.all });
      qc.invalidateQueries({ queryKey: queryKeys.quotes.detail(vars.id) });
    },
  });
}

export function useDeleteQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.del(`/quotes/${id}`),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: queryKeys.quotes.all });
    },
  });
}
